'use client'
import { FC, memo, ReactNode, useState, useEffect } from 'react'
import { AppCustomEvent } from '@types'
import useEventListener from '@use-it/event-listener'
import {
  RegisterToastEvent,
  ToastBaseOptions,
} from '@components/shared/toaster/toaster.ts'
import { ToastBar } from '@components/shared/toaster/ToastBar.tsx'

const activeToastOptions: {
  baseOptions: ToastBaseOptions
} = { baseOptions: {} }

const Toaster: FC = () => {
  const [activeToastJSX, setActiveToastJSX] = useState<ReactNode | null>(null)

  useEffect(() => {
    if (activeToastJSX && !activeToastOptions.baseOptions.forceKeepVisible) {
      const timer = setTimeout(() => {
        setActiveToastJSX(null)
        activeToastOptions.baseOptions = {}
      }, 7000)

      return () => clearTimeout(timer)
    }
  }, [activeToastJSX])

  const handleRegisterToastEvent = (event: Event) => {
    const toastEvent = event as RegisterToastEvent
    const { title, message, variant, forceKeepVisible, dismissOnLogout } =
      toastEvent.detail
    activeToastOptions.baseOptions = { forceKeepVisible, dismissOnLogout }

    setActiveToastJSX(<ToastBar title={title} message={message} variant={variant} />)
  }

  const handleRegisterCustomToastEvent = (event: Event) => {
    const { notificationJSX, forceKeepVisible, dismissOnLogout } = (
      event as CustomEvent<{ notificationJSX: ReactNode } & ToastBaseOptions>
    ).detail

    setActiveToastJSX(notificationJSX)
    activeToastOptions.baseOptions = { forceKeepVisible, dismissOnLogout }
  }

  const handleDismissToastEvent = () => {
    setActiveToastJSX(null)
    activeToastOptions.baseOptions = {}
  }

  const handleUserLoggedOutEvent = () => {
    if (
      activeToastOptions.baseOptions.dismissOnLogout &&
      !activeToastOptions.baseOptions.forceKeepVisible
    ) {
      setActiveToastJSX(null)
      activeToastOptions.baseOptions = {}
    }
  }

  const handleDismissCurrentToastEvent = () => {
    setActiveToastJSX(null)
    activeToastOptions.baseOptions = {}
  }

  useEventListener(
    AppCustomEvent.REGISTER_TOAST,
    handleRegisterToastEvent,
    typeof window !== 'undefined' ? window : null,
  )

  useEventListener(
    AppCustomEvent.DISMISS_ALL_TOASTS,
    handleDismissToastEvent,
    typeof window !== 'undefined' ? window : null,
  )

  useEventListener(
    AppCustomEvent.DISMISS_CURRENT_TOAST,
    handleDismissCurrentToastEvent,
    typeof window !== 'undefined' ? window : null,
  )

  useEventListener(
    AppCustomEvent.REGISTER_CUSTOM_TOAST,
    handleRegisterCustomToastEvent,
    typeof window !== 'undefined' ? window : null,
  )

  useEventListener(
    AppCustomEvent.USER_LOGGED_OUT,
    handleUserLoggedOutEvent,
    typeof window !== 'undefined' ? window : null,
  )

  return activeToastJSX
}

export default memo(Toaster)
